/* eslint-disable global-require */
/* eslint-disable @typescript-eslint/no-var-requires */
import Polyglot from 'node-polyglot';
import { getUserLocale } from 'get-user-locale';
import { RoleId } from 'core/types/role';

import { nb, sv, de, fr, pl } from 'date-fns/locale';
import enSet from 'date-fns/locale/en-GB';
import nbSet from 'date-fns/locale/nb';
import svSet from 'date-fns/locale/sv';
import deSet from 'date-fns/locale/de';
import frSet from 'date-fns/locale/fr';
import plSet from 'date-fns/locale/pl';

import { TranslationStrings } from './translation-types';
import { translateRole } from './roles';

export interface LanguageKeyValueNative {
  value: LocaleTypes;
  label: string;
  native: string;
}

function getLocale(lc: LocaleTypes): Locale {
  switch (lc) {
    case 'nb':
      return nb;
    case 'sv':
      return sv;
    case 'de':
      return de;
    case 'fr':
      return fr;
    case 'pl':
      return pl;
    default:
      return enSet;
  }
}

function getLocaleSet(lc: LocaleTypes): Locale {
  switch (lc) {
    case 'nb':
      return nbSet;
    case 'sv':
      return svSet;
    case 'de':
      return deSet;
    case 'fr':
      return frSet;
    case 'pl':
      return plSet;
    default:
      return enSet;
  }
}

export type LocaleTypes = 'en' | 'nb' | 'sv' | 'de' | 'fr' | 'pl';

export const availableLanguages: LanguageKeyValueNative[] = [
  {
    value: 'en',
    label: 'English',
    native: 'English',
  },
  {
    value: 'nb',
    label: 'Norwegian',
    native: 'Norsk (bokmål)',
  },
  {
    value: 'sv',
    label: 'Swedish',
    native: 'Svenska',
  },
  {
    value: 'de',
    label: 'German',
    native: 'Deutsche',
  },
  {
    value: 'fr',
    label: 'French',
    native: 'Français',
  },
  {
    value: 'pl',
    label: 'Polish',
    native: 'Polski',
  },
];

function getUserPreferedLanguage(): string {
  return localStorage.getItem('language-preference') || getUserLocale();
}

function getLangCodeAlpha2(): LocaleTypes {
  const locale = getUserPreferedLanguage().substring(0, 2) || 'en';
  console.log(`locale: ${getUserLocale()}`);

  switch (locale) {
    case 'no':
    case 'nb':
    case 'nn':
      return 'nb';
    case 'sv':
      return 'sv';
    case 'de':
      return 'de';
    case 'fr':
      return 'fr';
    case 'pl':
      return 'pl';
    default:
      return 'en';
  }
}

export const langcode = getLangCodeAlpha2();
export const langcode4 = getUserPreferedLanguage() || 'en_GB';

export const locale = getLocale(langcode);
export const localeSet = getLocaleSet(langcode);

type Common = typeof import('./locales/en/common.json');
type Logout = typeof import('./locales/en/logout.json');
type App = typeof import('./locales/en/app.json');
type Apps = typeof import('./locales/en/apps.json');
type Footer = typeof import('./locales/en/footer.json');
type Core = typeof import('./locales/en/core.json');
type Components = typeof import('./locales/en/components.json');
type Pages = typeof import('./locales/en/pages.json');
type Roles = typeof import('./locales/en/roles.json');
type Videos = typeof import('./locales/en/videos.json');

type Section =
  | Common
  | Logout
  | App
  | Apps
  | Footer
  | Core
  | Components
  | Pages
  | Roles
  | Videos;
type SectionTypes =
  | 'common'
  | 'logout'
  | 'app'
  | 'apps'
  | 'footer'
  | 'core'
  | 'components'
  | 'pages'
  | 'roles'
  | 'videos';

export type Locales = {
  [key in SectionTypes]: Section;
};

type Phrases = { [key in LocaleTypes]: Locales };

const translations: Phrases = {
  en: {
    common: require('./locales/en/common.json') as Common,
    logout: require('./locales/en/logout.json') as Logout,
    app: require('./locales/en/app.json') as App,
    apps: require('./locales/en/apps.json') as Apps,
    footer: require('./locales/en/footer.json') as Footer,
    core: require('./locales/en/core.json') as Core,
    components: require('./locales/en/components.json') as Components,
    pages: require('./locales/en/pages.json') as Pages,
    roles: require('./locales/en/roles.json') as Roles,
    videos: require('./locales/en/videos.json') as Videos,
  },
  nb: {
    common: require('./locales/nb/common.json') as Common,
    logout: require('./locales/nb/logout.json') as Logout,
    app: require('./locales/nb/app.json') as App,
    apps: require('./locales/nb/apps.json') as Apps,
    footer: require('./locales/nb/footer.json') as Footer,
    core: require('./locales/nb/core.json') as Core,
    components: require('./locales/nb/components.json') as Components,
    pages: require('./locales/nb/pages.json') as Pages,
    roles: require('./locales/nb/roles.json') as Roles,
    videos: require('./locales/nb/videos.json') as Videos,
  },
  sv: {
    common: require('./locales/sv/common.json') as Common,
    logout: require('./locales/sv/logout.json') as Logout,
    app: require('./locales/sv/app.json') as App,
    apps: require('./locales/sv/apps.json') as Apps,
    footer: require('./locales/sv/footer.json') as Footer,
    core: require('./locales/sv/core.json') as Core,
    components: require('./locales/sv/components.json') as Components,
    pages: require('./locales/sv/pages.json') as Pages,
    roles: require('./locales/sv/roles.json') as Roles,
    videos: require('./locales/sv/videos.json') as Videos,
  },
  de: {
    common: require('./locales/de/common.json') as Common,
    logout: require('./locales/de/logout.json') as Logout,
    app: require('./locales/de/app.json') as App,
    apps: require('./locales/de/apps.json') as Apps,
    footer: require('./locales/de/footer.json') as Footer,
    core: require('./locales/de/core.json') as Core,
    components: require('./locales/de/components.json') as Components,
    pages: require('./locales/de/pages.json') as Pages,
    roles: require('./locales/de/roles.json') as Roles,
    videos: require('./locales/de/videos.json') as Videos,
  },
  fr: {
    common: require('./locales/fr/common.json') as Common,
    logout: require('./locales/fr/logout.json') as Logout,
    app: require('./locales/fr/app.json') as App,
    apps: require('./locales/fr/apps.json') as Apps,
    footer: require('./locales/fr/footer.json') as Footer,
    core: require('./locales/fr/core.json') as Core,
    components: require('./locales/fr/components.json') as Components,
    pages: require('./locales/fr/pages.json') as Pages,
    roles: require('./locales/fr/roles.json') as Roles,
    videos: require('./locales/fr/videos.json') as Videos,
  },
  pl: {
    common: require('./locales/pl/common.json') as Common,
    logout: require('./locales/pl/logout.json') as Logout,
    app: require('./locales/pl/app.json') as App,
    apps: require('./locales/pl/apps.json') as Apps,
    footer: require('./locales/pl/footer.json') as Footer,
    core: require('./locales/pl/core.json') as Core,
    components: require('./locales/pl/components.json') as Components,
    pages: require('./locales/pl/pages.json') as Pages,
    roles: require('./locales/pl/roles.json') as Roles,
    videos: require('./locales/pl/videos.json') as Videos,
  },
};

export const phrases = translations[langcode] || translations.en;

const polyglot = new Polyglot({ locale: langcode, phrases });

const p = {
  t: (
    k: TranslationStrings,
    o?: number | Polyglot.InterpolationOptions | undefined,
  ): string => polyglot.t(k, o),
  setLanguage: (newLangcode: LocaleTypes): void => {
    polyglot.locale(newLangcode);
    polyglot.replace(translations[newLangcode]);
  },
};

export default p;

export function tRole(id: RoleId): string {
  return p.t(translateRole(id));
}
